import { apiService } from './ApiService';

/**
 * OAuth 2.0 authorization-code consent flow (MCP clients).
 *
 * The backend `/api/v1/oauth/authorize` endpoint redirects the browser here
 * (the `/oauth/consent` route). After the logged-in user approves, we call
 * `/authorize/approve` — the request carries the user's Firebase token via the
 * shared apiService interceptor — and the backend returns the client redirect
 * URL (a localhost callback the MCP client is listening on) to navigate to.
 */

export interface ConsentParams {
  client_id: string;
  client_name?: string;
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: string;
  scope?: string;
  state?: string;
  resource?: string;
}

export interface ApproveResponse {
  redirect_to: string;
}

/** Parse the consent parameters off the current URL query string. */
export function parseConsentParams(search: string): ConsentParams | null {
  const q = new URLSearchParams(search);
  const client_id = q.get('client_id');
  const redirect_uri = q.get('redirect_uri');
  const code_challenge = q.get('code_challenge');
  if (!client_id || !redirect_uri || !code_challenge) {
    return null;
  }
  return {
    client_id,
    client_name: q.get('client_name') || undefined,
    redirect_uri,
    code_challenge,
    code_challenge_method: q.get('code_challenge_method') || 'S256',
    scope: q.get('scope') || undefined,
    state: q.get('state') || undefined,
    resource: q.get('resource') || undefined,
  };
}

/**
 * Resolve the origin of the OAuth API host.
 *
 * The OAuth/MCP endpoints live in the public API service (catax_api), which is
 * a DIFFERENT origin from the one serving this SPA (the AI service builds the
 * bundle with REACT_APP_API_URL='' for same-origin calls). We therefore cannot
 * use a relative path here — a relative POST hits the SPA host and 405s.
 *
 * The RFC 8707 `resource` param IS the MCP server URL on the API host
 * (e.g. https://api-dev.catax.ai/api/v1/mcp), so its origin is exactly the host
 * that owns `/api/v1/oauth/authorize/approve`. This makes the consent flow
 * self-configuring across environments without any build-time env var.
 */
function oauthApiOrigin(resource?: string): string {
  if (resource) {
    try {
      return new URL(resource).origin;
    } catch {
      /* malformed resource — fall through */
    }
  }
  return process.env.REACT_APP_API_URL || '';
}

/** Submit the user's consent decision; returns the URL to redirect the browser to. */
export async function submitConsent(
  params: ConsentParams,
  approved: boolean,
): Promise<string> {
  const base = oauthApiOrigin(params.resource);
  const res = await apiService.post<ApproveResponse>(
    `${base}/api/v1/oauth/authorize/approve`,
    { ...params, approved },
  );
  return res.data.redirect_to;
}

/** Human-readable labels for the MCP scopes shown on the consent screen. */
export const SCOPE_LABELS: Record<string, string> = {
  'mcp:chat': 'Chat with Cata — ask questions and run conversations',
  'mcp:vehicles': 'Identify vehicles by VIN',
  'mcp:parts': 'Search the parts catalog',
};
